import { CSSProperties, ReactElement, RefObject, useRef } from "react";
import { NavLink, useParams } from "react-router-dom";

import { HeaderNavElement } from "@/components/_molecules/header-nav-element";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyRegisterContentQuery } from "@/graphql/generated/graphql";
import { cn } from "@/lib/cn";
import { useVirtualizer, VirtualItem, Virtualizer } from "@tanstack/react-virtual";

const ESTIMATED_ITEM_HEIGHT_PX: number = 64;
const COMPACT_ITEM_HEIGHT_PX = 28;
const COMPACT_ITEM_GAP_PX = 4;
const VIRTUALIZATION_THRESHOLD: number = 100;
const VIRTUAL_ITEM_OVERSCAN: number = 10;

type MembershipSummary = MunicipalPropertyRegisterContentQuery["municipalPropertyYearList"]["nodeList"][number];

export function MunicipalPropertyYearNavigation({
  yearList,
  hasNextPage = false,
  selection,
}: {
  yearList: readonly MunicipalPropertyRegisterContentQuery["municipalPropertyYearList"]["nodeList"][number][];
  hasNextPage?: boolean;
  selection?: {
    makeLink: (yearId: string) => string;
    selectedPropertyYearId: string;
  };
}): ReactElement {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const { propertyYearId }: { propertyYearId?: string } = useParams<{ propertyYearId: string }>();
  const scrollElementRef: RefObject<HTMLElement> = useRef<HTMLElement>(null);
  const listStyle: CSSProperties & { "--property-navigation-gap": string } = {
    "--property-navigation-gap": `${COMPACT_ITEM_GAP_PX}px`,
  };
  const isVirtualized: boolean = yearList.length > VIRTUALIZATION_THRESHOLD;
  const itemVirtualizer: Virtualizer<HTMLElement, HTMLLIElement> = useVirtualizer<HTMLElement, HTMLLIElement>({
    count: isVirtualized ? yearList.length : 0,
    getScrollElement: (): HTMLElement | null => scrollElementRef.current,
    estimateSize: (): number => selection ? COMPACT_ITEM_HEIGHT_PX : ESTIMATED_ITEM_HEIGHT_PX,
    gap: selection ? COMPACT_ITEM_GAP_PX : 0,
    getItemKey: (index: number): string => yearList[index].id,
    overscan: VIRTUAL_ITEM_OVERSCAN,
  });
  const virtualItemList: VirtualItem[] = isVirtualized ? itemVirtualizer.getVirtualItems() : [];
  const visibleYearList: readonly MembershipSummary[] = isVirtualized
    ? virtualItemList.map(({ index }: VirtualItem): MembershipSummary => yearList[index])
    : yearList;

  return (
    <nav
      ref={scrollElementRef}
      aria-label={t("register.property.name")}
      className="max-h-60 min-h-0 overflow-auto md:max-h-none md:flex-1"
    >
      <ul
        className={selection ? "flex flex-col gap-[var(--property-navigation-gap)]" : undefined}
        style={{
          ...listStyle,
          ...(isVirtualized ? {
            paddingTop: virtualItemList[0]?.start ?? 0,
            paddingBottom: itemVirtualizer.getTotalSize() - (virtualItemList.at(-1)?.end ?? 0),
          } : {}),
        }}
      >
        {visibleYearList.map(function renderMembership(year: MembershipSummary, index: number): ReactElement {
          const label = typeof year.effectiveYear !== "number" ? t("register.property.missingStand") : year.name;
          return (
            <li
              key={year.id}
              aria-setsize={isVirtualized ? (hasNextPage ? -1 : yearList.length) : undefined}
              aria-posinset={isVirtualized ? virtualItemList[index].index + 1 : undefined}
              ref={isVirtualized ? itemVirtualizer.measureElement : undefined}
              data-index={isVirtualized ? virtualItemList[index].index : undefined}
            >
              {selection ? <HeaderNavElement
                variant="pill" isSelected={selection.selectedPropertyYearId === year.id}
                to={selection.makeLink(year.id)}>
                <span className="truncate" title={label}>{label}</span>
              </HeaderNavElement> : <NavLink
                to={`${year.id}/data`}
                className={cn(
                  "flex min-h-16 flex-col justify-center gap-1 rounded-md px-3 py-2 text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                  propertyYearId === year.id && "bg-muted font-medium",
                )}
              >
                <span className="break-words">
                  {label}
                </span>
                {year.type ? <span className="text-xs text-muted-foreground">{t(`type.${year.type}`)}</span> : null}
              </NavLink>}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
